import { OrderIdsInfo } from '@cart-ui/OrderIdsInfo/OrderIdsInfo'
import { DATE_FORMAT, TIME_FORMAT } from '@constants'
import { type RefundStatus } from '@hooks-api/types/dataModel/eCommerceOrders'
import { FastpassOrderStatus, type ChildOrder } from '@hooks-api/types/dataModel/fpOrder'
import { PaymentMethodMap, type PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { deriveDisplayStatus } from '@utils/derive-display-status'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useContextOrderDetail } from '../OrderDetailProvider/OrderDetailProvider'
import { DialogConfirmCancelTransaction } from './DialogConfirmCancelTransaction'
import { RefundStatusBadge } from './RefundStatusBadge'
const styles = stylex.create({
  boxWrapper: {
    gap: 16,
    paddingTop: 8,
  },
  paymentButton: {
    padding: 4,
    marginTop: 8,
  },
  cancel: {
    padding: 0,
  },
})

interface OrderSummaryProps {
  transactionId?: string
  orderCreatedTime?: string
  orders?: ChildOrder[]
  status?: FastpassOrderStatus
  paymentMethod?: PaymentMethodType
  refundStatus?: RefundStatus | null
}

const OLD_ORDER_ID = -1

export function OrderSummary({ transactionId, orderCreatedTime, orders, status, paymentMethod: paymentMethodProps, refundStatus }: OrderSummaryProps) {
  const { t } = useTranslation()
  const { currency, onPayment, onCancel, environment } = useContextOrderDetail()
  const isMerchant = environment === 'merchant'
  const canRepayment = (status === FastpassOrderStatus.FAILED || status === FastpassOrderStatus.PENDING) && onPayment
  const isPaymentPending = status === FastpassOrderStatus.PENDING
  const isTransaction = transactionId !== null && Number(transactionId) !== OLD_ORDER_ID
  // 暫解：後端尚未回傳 display_status 時，前端根據 order status 推導
  const siblingStatuses = orders?.map(sibling => sibling.status)
  return (
    <>
      <Paper p={3} borderRadius={8} elevation={0}>
        <HStack justifyContent="space-between">
          <Text variant="subtitle">{t('orderDetail.orderSummary', { defaultValue: '訂購摘要' })}</Text>
          {onCancel && (
            <Button variant="text" rootStyle={styles.cancel} onClick={onCancel} disabled={!isPaymentPending}>
              <Text color={isPaymentPending ? colors.blue600 : colors.grey300}>{t('orderDetail.cancelOrder', { defaultValue: '取消訂單' })}</Text>
            </Button>
          )}
        </HStack>
        <HStack rootStyle={styles.boxWrapper}>
          <Text color={colors.grey600}>{t('orderDetail.transactionId', { defaultValue: '交易編號' })}</Text>
          <Text>{isTransaction ? transactionId : '-'}</Text>
        </HStack>
        <HStack rootStyle={styles.boxWrapper}>
          <Text color={colors.grey600}>{t('orderDetail.orderDate', { defaultValue: '訂單日期' })}</Text>
          {orderCreatedTime && <Text>{format(new Date(orderCreatedTime), DATE_FORMAT + ' ' + TIME_FORMAT)}</Text>}
        </HStack>
        <HStack rootStyle={styles.boxWrapper}>
          <Text color={colors.grey600}>{t('orderDetail.orderId', { defaultValue: '訂單編號' })}</Text>
          <Box>
            <VStack gap={1}>
              {orders?.map((order) => {
                const resolvedDisplayStatus = order.display_status ?? deriveDisplayStatus({
                  status: order.status,
                  refundStatus,
                  relatedOrderStatuses: siblingStatuses,
                })
                return (
                  <OrderIdsInfo
                    key={order.id}
                    receiverName={order.shipping_address_first_name}
                    orderId={order.order_id}
                    displayStatus={t(`orderDetail.displayStatus.${resolvedDisplayStatus}`, { defaultValue: resolvedDisplayStatus })}
                    total={order.total}
                    paymentMethod={paymentMethodProps ? PaymentMethodMap[paymentMethodProps] : ''}
                    shippingMethodType={order.shipping_method}
                    currency={currency}
                  />
                )
              })}
              {refundStatus && <RefundStatusBadge refundStatus={refundStatus} isMerchant={isMerchant} />}
            </VStack>
            {canRepayment
            && (
              <Button
                variant="outlined"
                color="grey"
                rootStyle={styles.paymentButton}
                disabled={!canRepayment}
                onClick={onPayment}
              >
                {t('orderDetail.payNow', { defaultValue: '立即付款' })}
              </Button>
            )}
          </Box>
        </HStack>
      </Paper>
      <DialogConfirmCancelTransaction />
    </>
  )
}
